import { useSuspenseQuery } from '@tanstack/react-query';
import { SourcingMetricsFilterRange, SourcingService } from 'api-clients/monolith';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { AggregateDataCheckbox } from '@/components/ui/aggregate-data-checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { useShowHiringGoalEmptyState } from '@/components/pages/channels/use-show-hiring-goal-empty-state';
import { queryKeys } from '@/query-keys';

import { AggregateView } from './aggregate-view';

interface SourcingOpeningsProps {
  defaultMetricsPeriod?: SourcingMetricsFilterRange | null;
}

export function SourcingOpenings({ defaultMetricsPeriod }: SourcingOpeningsProps) {
  const intl = useIntl();
  const openingSlug = useOpeningSlug();
  const { hiringGoalId, startDate, endDate, filterRange, displayPrefs } =
    useOpeningContext();
  const { setDisplayPrefs } = displayPrefs;

  // Kick this fetch off BEFORE `useOpeningDetails` below so it fires in
  // parallel with the funnel-details API instead of waiting for it.
  const { data, isError } = useSuspenseQuery({
    queryKey: [
      ...queryKeys.openingDetails.channels.sourcingFunnels,
      openingSlug,
      hiringGoalId,
      displayPrefs.showAggregateHires,
      displayPrefs.showAggregateSpend,
      startDate,
      endDate,
      filterRange,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsSourcingFunnels(
        openingSlug,
        hiringGoalId,
        displayPrefs.showAggregateHires,
        displayPrefs.showAggregateSpend,
        startDate,
        endDate,
        filterRange,
      ),
  });

  if (isError) throw new Error('Failed to fetch sourcing funnels');

  const { hiringGoal } = useOpeningDetails();
  const showHiringGoalEmptyState = useShowHiringGoalEmptyState(hiringGoal);

  const sourcingOpenings = useMemo(() => data.sourcing_funnels, [data]);
  const aggregateData = useMemo(
    () => (data && data.aggregate ? [data.aggregate] : []),
    [data],
  );

  useEffect(() => {
    if (sourcingOpenings.length > 0) {
      setDisplayPrefs({ showAggregateHiresCheckbox: true });
    }
  }, [setDisplayPrefs, sourcingOpenings]);

  if (showHiringGoalEmptyState) return null;
  if (aggregateData.length === 0) return null;

  return (
    <>
      <div className="flex justify-end">
        <AggregateDataCheckbox
          checked={displayPrefs.showAggregateSpend}
          onCheckedChange={value =>
            setDisplayPrefs({ showAggregateSpend: !!value })
          }
          label={intl.formatMessage({
            defaultMessage: 'Include this to “Budget Spend”',
            description:
              'Checkbox: add aggregate other openings’ spend into “Budget spend” for this view',
          })}
        />
      </div>
      <div className="w-full">
        <div className="overflow-x-auto">
          <div className="min-w-max gap-1">
            <div className="space-y-4 pb-2">
              <AggregateView
                aggregateData={aggregateData}
                sourcingOpenings={sourcingOpenings}
                defaultMetricsPeriod={defaultMetricsPeriod}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

SourcingOpenings.Skeleton = function SourcingOpeningsSkeleton() {
  return (
    <>
      <div className="flex justify-end">
        <Skeleton className="mb-1 h-5 w-53" />
      </div>
      <div className="w-full">
        <div className="overflow-x-auto">
          <div className="min-w-max gap-1">
            <div className="space-y-4 pb-2">
              <AggregateView.Skeleton />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
